import type { TFunction } from "i18next";

/**
 * Los títulos autogenerados de tarea se guardaron ya traducidos.
 *
 * `VoiceTaskConfirmation` compone `${t("planner.types.<tipo>")} — <ubicación>`,
 * así que la palabra del tipo se congela en el idioma que tuviera quien creó la
 * tarea y ahí se queda para siempre: un equipo mixto acaba viendo "Treatment —
 * Campohermoso · Nave 4" en una app puesta en español.
 *
 * La ubicación NO se traduce nunca — Las Norias, Nave 2 y C son nombres
 * propios, iguales en todos los idiomas. Lo único que sobra es esa primera
 * palabra, y es la que reescribimos al pintar.
 *
 * Un título escrito a mano no se toca: solo se reescribe si el prefijo es
 * EXACTAMENTE una etiqueta de tipo conocida y va seguida de " — " o ": ".
 */

/**
 * Etiquetas de tipo en los NUEVE idiomas. Es una tabla literal a propósito: los
 * bundles de idioma se cargan de forma perezosa (`src/i18n/config.ts` solo trae
 * el idioma activo), así que en tiempo de ejecución NO se puede preguntar a
 * i18next cómo se dice "Treatment" en italiano si el usuario está en español.
 *
 * Si se añade un idioma o se cambia una etiqueta de `planner.types`, hay que
 * tocar esta tabla. El test de paridad lo comprueba contra los JSON.
 *
 * Orden de cada fila: en · es · it · ar · bm · man · fr · pt · zh. Se omite la
 * etiqueta que ya esté puesta por otro idioma (pt y es comparten «Visita», en y
 * fr comparten «Inspection»): la tabla es un buscador, no un diccionario, y
 * repetir no aporta nada.
 */
const TYPE_LABELS: Record<string, string[]> = {
  treatment: ["Treatment", "Tratamiento", "Trattamento", "معالجة", "Kɛrɛngkɛrɛnna", "Daawo", "Traitement", "Tratamento", "防治"],
  monitoring: ["Monitoring", "Monitoreo", "Monitoraggio", "مراقبة", "Kɔlɔsili", "Lele Kooroo", "Suivi", "Monitorização", "监测"],
  maintenance: ["Maintenance", "Mantenimiento", "Manutenzione", "صيانة", "Kunnafonabɔ", "Laakanoo", "Entretien", "Manutenção", "维护"],
  harvest: ["Harvest", "Cosecha", "Raccolta", "حصاد", "Suman tigɛ", "Sùmanitigɛ", "Récolte", "Colheita", "采收"],
  visit: ["Visit", "Visita", "زيارة", "Sɛgɛsɛgɛli", "Fenda", "Visite", "走访"],
  inspection: ["Inspection", "Inspección", "Ispezione", "تفتيش", "Filɛli", "Lele", "Inspeção", "巡检"],
  other: ["Other", "Otro", "Altro", "أخرى", "Wɛrɛ", "Wolu", "Autre", "Outro", "其他"],
};

/** etiqueta normalizada → clave de tipo. Se construye una sola vez. */
const LABEL_TO_KEY: Map<string, string> = (() => {
  const map = new Map<string, string>();
  for (const [key, labels] of Object.entries(TYPE_LABELS)) {
    for (const label of labels) {
      const norm = label.trim().toLowerCase();
      // Colisión entre idiomas (p. ej. "Visita" es visit en es y en it): la
      // primera gana, que es la que comparte significado de todas formas.
      if (!map.has(norm)) map.set(norm, key);
    }
  }
  return map;
})();

/** Separadores que usan los generadores automáticos, en orden de preferencia. */
const SEPARATORS = [" — ", ": "];

/**
 * ¿Este título lo escribió una máquina? Si sí, de qué tipo de tarea es.
 *
 * Devuelve la clave del tipo (`treatment`, `maintenance`…) cuando el título
 * sigue el patrón que generan el dictado y los asistentes —una etiqueta de tipo
 * conocida, en cualquiera de los nueve idiomas, seguida de « — » o «: »— y
 * `null` cuando lo escribió una persona.
 *
 * No hay ninguna columna en la base de datos que lo diga, así que se reconoce
 * por la forma. Es conservador a propósito: ante la duda, un título es de quien
 * lo escribió.
 */
export function autoTitleTypeKey(title: string | null | undefined): string | null {
  if (!title) return null;
  for (const sep of SEPARATORS) {
    const at = title.indexOf(sep);
    if (at <= 0) continue;
    const key = LABEL_TO_KEY.get(title.slice(0, at).trim().toLowerCase());
    if (key) return key;
  }
  /* Y EL TÍTULO QUE ES SOLO LA ETIQUETA (05-09-2026). Desde que el dictado dejó
     de meter la ubicación, un título automático puede ser «Tratamiento» a secas,
     sin separador detrás. Sin este caso se quedaría congelado en el idioma de
     quien lo dictó, que es justo lo que este fichero existe para evitar.
     Es igual de conservador: tiene que ser la etiqueta ENTERA y nada más, así
     que un «Tratamiento con azufre» escrito a mano no entra. */
  return LABEL_TO_KEY.get(title.trim().toLowerCase()) ?? null;
}

/**
 * Devuelve el título con la palabra del tipo en el idioma actual. Si el título
 * no encaja con el patrón autogenerado, lo devuelve TAL CUAL — un título que
 * escribió una persona es suyo y no se traduce.
 */
export function translateTaskTitle(
  title: string | null | undefined,
  t: TFunction,
): string {
  if (!title) return title ?? "";
  const key = autoTitleTypeKey(title);
  if (!key) return title;
  /* Título que es SOLO la etiqueta: se traduce entero y no hay nada que pegarle
     detrás. */
  if (LABEL_TO_KEY.get(title.trim().toLowerCase()) === key) {
    return t(`planner.types.${key}`, title);
  }
  for (const sep of SEPARATORS) {
    const at = title.indexOf(sep);
    if (at <= 0) continue;
    if (LABEL_TO_KEY.get(title.slice(0, at).trim().toLowerCase()) !== key) continue;
    const prefix = title.slice(0, at);
    return `${t(`planner.types.${key}`, prefix)}${sep}${title.slice(at + sep.length)}`;
  }
  return title;
}

/** Solo para el test de paridad contra los JSON de idioma. */
export const __TYPE_LABELS = TYPE_LABELS;

/**
 * El NOMBRE de una tarea en las listas compactas: la tarjeta del día del
 * planificador y las tareas de un miembro del equipo. De un título automático
 * («Tratamiento: Mosca blanca», «Tratamiento — Las Norias · Nave 4») se queda solo
 * el tipo, en el idioma de quien mira; un título escrito a mano se respeta entero.
 * Vive aquí para que esas listas no puedan nombrar la misma tarea de dos maneras.
 */
export function taskDisplayName(title: string | null | undefined, t: TFunction): string {
  const tipo = autoTitleTypeKey(title);
  return tipo ? t(`planner.types.${tipo}`) : translateTaskTitle(title, t);
}

/**
 * EL TÍTULO QUE PONE LA APP CUANDO NADIE LO ESCRIBE (usuario 14-09-2026).
 *
 * La regla, entera:
 *   · tratamiento con plaga ........................ «Tratamiento: Mosca blanca»
 *   · sin plaga, con un tratamiento configurado ..... «Tratamiento: Spintor»
 *   · sin plaga ni tratamiento configurado ......... «Tratamiento»
 *   · cualquier otro tipo .......................... solo el tipo: «Monitoreo»…
 *
 * NUNCA la urgencia ni la ubicación: las dos tienen su propia columna y cada
 * pantalla las pinta donde le toca. El separador es «: », que `autoTitleTypeKey`
 * reconoce como título de máquina; así la palabra del tipo se traduce al pintarla.
 *
 * Los títulos que ya existen se quedan como están (decisión del usuario). El
 * servidor no puede importar este archivo y lleva su copia en
 * `supabase/functions/_shared/taskTitle.ts`; `autoTaskTitle.test.ts` comprueba
 * que las dos dan exactamente lo mismo.
 */
export interface AutoTaskTitleInput {
  type: string | null | undefined;
  pestName?: string | null;
  /** Solo un tratamiento CONFIGURADO (una receta), no una lista de productos a mano. */
  treatmentName?: string | null;
  /** Tratamiento con productos a mano: la tarea no guarda plaga ni receta, así que
   *  el título es «Tratamiento» a secas aunque se hubieran dicho. */
  customTreatment?: boolean;
}

/** El tipo con el que se escribe. `scouting` no tiene rótulo propio: es monitoreo
 *  de plagas. Un tipo que la app no conoce se escribe como «Otro». */
function autoTitleLabelKey(type: string | null | undefined): string {
  const key = (type || "").toLowerCase();
  if (key === "scouting") return "monitoring";
  return key in TYPE_LABELS ? key : "other";
}

export function buildAutoTaskTitle(input: AutoTaskTitleInput, t: (key: string) => string): string {
  const key = autoTitleLabelKey(input.type);
  const i18nKey = `planner.types.${key}`;
  const translated = t(i18nKey);
  // TYPE_LABELS[key][1] es la etiqueta en español: la de respaldo si falta la traducción.
  const label = translated && translated !== i18nKey ? translated : TYPE_LABELS[key][1];
  if (key !== "treatment" || input.customTreatment) return label;
  const detail = input.pestName?.trim() || input.treatmentName?.trim();
  return detail ? `${label}: ${detail}` : label;
}
